import { Color, IfcAPI, PlacedGeometry } from 'web-ifc'
import {
  Color3,
  IndicesArray,
  Matrix,
  Mesh,
  Scene,
  StandardMaterial,
  TransformNode,
  VertexData,
} from '@babylonjs/core'

export class IfcLoader {
  private ifcAPI: IfcAPI
  private isInitialized = false
  private static instance: IfcLoader | null = null

  static getInstance(): IfcLoader {
    if (!IfcLoader.instance) {
      IfcLoader.instance = new IfcLoader()
    }
    return IfcLoader.instance
  }

  async load(file: Uint8Array, scene: Scene, mergematerials: boolean) {
    if (!this.isInitialized) {
      await this.initialize()
    }

    let mToggle_YZ = [1, 0, 0, 0, 0, -1, 0, 0, 0, 0, -1, 0, 0, 0, 0, -1]

    let modelID = this.ifcAPI.OpenModel(file)
    this.ifcAPI.SetGeometryTransformation(modelID, mToggle_YZ)
    let flatMeshes = this.getFlatMeshes(modelID)

    let nodecount = 0
    let currentnode = 0
    let mainObject = new Mesh('IFC Model', scene)
    let meshnode

    let meshmaterials = new Map<number, Mesh>()

    for (let i = 0; i < flatMeshes.size(); i++) {
      let placedGeometries = flatMeshes.get(i).geometries
      if (nodecount++ % 100 === 0) {
        currentnode++
        meshnode = new TransformNode('node' + currentnode, scene)
        meshnode.parent = mainObject
        meshmaterials = new Map<number, Mesh>()
      }
      for (let j = 0; j < placedGeometries.size() - 1; j++) {
        this.getPlacedGeometry(
          modelID,
          placedGeometries.get(j),
          scene,
          meshnode ?? null,
          meshmaterials,
          mergematerials,
        )
      }
    }

    return mainObject
  }

  dispose() {
    try {
      if (this.isInitialized) {
        this.ifcAPI.Dispose()
        IfcLoader.instance = null
      }
    } catch (error) {
      console.error(error)
    }
  }

  private constructor() {
    this.ifcAPI = new IfcAPI()
  }

  private async initialize() {
    this.ifcAPI.SetWasmPath('../../')
    await this.ifcAPI.Init()
    this.isInitialized = true
  }

  private getFlatMeshes(modelID: number) {
    let flatMeshes = this.ifcAPI.LoadAllGeometry(modelID)
    return flatMeshes
  }

  private getPlacedGeometry(
    modelID: number,
    placedGeometry: PlacedGeometry,
    scene: Scene,
    mainObject: TransformNode | null,
    meshmaterials: Map<number, Mesh>,
    mergematerials: boolean,
  ) {
    let meshgeometry = this.getBufferGeometry(modelID, placedGeometry, scene)
    if (meshgeometry != null) {
      let m = placedGeometry.flatTransformation

      let matrix = new Matrix()
      matrix.setRowFromFloats(0, m[0], m[1], m[2], m[3])
      matrix.setRowFromFloats(1, m[4], m[5], m[6], m[7])
      matrix.setRowFromFloats(2, m[8], m[9], m[10], m[11])
      matrix.setRowFromFloats(3, m[12], m[13], m[14], m[15])

      // Some IFC files are not parsed correctly, leading to degenerated meshes
      try {
        meshgeometry.bakeTransformIntoVertices(matrix)
      } catch {
        console.warn(
          'Unable to bake transform matrix into vertex array. Some elements may be in the wrong position.',
        )
      }

      let color = placedGeometry.color
      let colorid: number =
        Math.floor(color.x * 256) +
        Math.floor(color.y * 256 ** 2) +
        Math.floor(color.z * 256 ** 3) +
        Math.floor(color.w * 256 ** 4)

      if (mergematerials && meshmaterials.has(colorid)) {
        let tempmesh = meshmaterials.get(colorid)

        if (tempmesh) {
          meshgeometry.material = tempmesh.material
          let mergedmesh = Mesh.MergeMeshes(
            [tempmesh, meshgeometry],
            true,
            true,
          )
          if (mergedmesh) {
            mergedmesh.name = colorid.toString(16)
            mergedmesh.material?.freeze()
            meshmaterials.set(colorid, mergedmesh)
            mergedmesh.parent = mainObject
          }
        }
      } else {
        let newMaterial = this.getMeshMaterial(color, scene)
        meshgeometry.material = newMaterial
        meshgeometry.material?.freeze()
        meshmaterials.set(colorid, meshgeometry)
        meshgeometry.parent = mainObject
      }

      return meshgeometry
    } else return null
  }

  private getBufferGeometry(
    modelID: number,
    placedGeometry: PlacedGeometry,
    scene: Scene,
  ) {
    let geometry = this.ifcAPI.GetGeometry(
      modelID,
      placedGeometry.geometryExpressID,
    )
    if (geometry.GetVertexDataSize() !== 0) {
      let vertices = this.ifcAPI.GetVertexArray(
        geometry.GetVertexData(),
        geometry.GetVertexDataSize(),
      )
      let indices = this.ifcAPI.GetIndexArray(
        geometry.GetIndexData(),
        geometry.GetIndexDataSize(),
      )

      let mesh = new Mesh('custom', scene)
      let vertexData = this.getVertexData(vertices, indices)
      vertexData.applyToMesh(mesh, false)

      return mesh
    } else return null
  }

  private getVertexData(vertices: Float32Array, indices: IndicesArray) {
    let positions = new Array(Math.floor(vertices.length / 2))
    let normals = new Array(Math.floor(vertices.length / 2))
    for (let i = 0; i < vertices.length / 6; i++) {
      positions[i * 3 + 0] = vertices[i * 6 + 0]
      positions[i * 3 + 1] = vertices[i * 6 + 1]
      positions[i * 3 + 2] = vertices[i * 6 + 2]
      normals[i * 3 + 0] = vertices[i * 6 + 3]
      normals[i * 3 + 1] = vertices[i * 6 + 4]
      normals[i * 3 + 2] = vertices[i * 6 + 5]
    }
    let vertexData = new VertexData()
    vertexData.positions = positions
    vertexData.normals = normals
    vertexData.indices = indices

    return vertexData
  }

  private getMeshMaterial(color: Color, scene: Scene) {
    const colorid: number =
      Math.floor(color.x * 256) +
      Math.floor(color.y * 256 ** 2) +
      Math.floor(color.z * 256 ** 3) +
      Math.floor(color.w * 256 ** 4)
    let myMaterial = new StandardMaterial(colorid.toString(), scene)

    myMaterial.diffuseColor = new Color3(color.x, color.y, color.z)
    myMaterial.alpha = color.w
    myMaterial.sideOrientation = Mesh.DOUBLESIDE
    myMaterial.backFaceCulling = false

    return myMaterial
  }
}
